import { useEffect, useState, useCallback, useMemo, memo } from "react";
import api from "../../api/api";
import Swal from "sweetalert2";
import styles from "./Usuarios.module.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLES = ["admin", "vendedor", "estoque"];

const ROLE_LABELS = {
  admin:    "Gerente",
  vendedor: "Vendedor(a)",
  estoque:  "Operador de Estoque",
};

const EMPTY_FORM = {
  id:           null,
  nome:         "",
  login:        "",
  senha:        "",
  cargo:        "",
  nivel_acesso: "vendedor",
  cliente_id:   null,
};

// ─── Services ─────────────────────────────────────────────────────────────────

const usuariosService = {
  getAll:    ()        => api.get("/usuarios"),
  create:    (payload) => api.post("/usuarios/register", payload),
  update:    (id, payload) => api.put(`/usuarios/${id}`, payload),
  deactivate:(id)      => api.put(`/usuarios/${id}/desativar`),
  activate:  (id)      => api.put(`/usuarios/${id}/ativar`),
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

const useUsuarios = () => {
  const [usuarios, setUsuarios]   = useState([]);
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await usuariosService.getAll();
      setUsuarios(data);
    } catch (err) {
      setError("Não foi possível carregar os usuários.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { usuarios, loading, error, refetch: fetch };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildPayload = (form, userLogado) => {
  const payload = { ...form };

  // Remove senha do payload na edição se não foi preenchida
  if (form.id && !form.senha) delete payload.senha;

  // Garante cliente_id correto conforme nível de acesso
  payload.cliente_id =
    userLogado?.nivel_acesso === "superadmin"
      ? form.cliente_id ?? null
      : userLogado?.cliente_id;

  return payload;
};

const confirm = (opts) => Swal.fire({ showCancelButton: true, ...opts });

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Badge de perfil colorido */
const RoleBadge = memo(({ role }) => (
  <span className={`${styles.badge} ${styles[`badge_${role}`]}`}>
    {ROLE_LABELS[role] ?? role}
  </span>
));
RoleBadge.displayName = "RoleBadge";

/** Badge de status ativo/inativo */
const StatusBadge = memo(({ ativo }) => (
  <span className={`${styles.statusBadge} ${ativo ? styles.statusActive : styles.statusInactive}`}>
    <span className={styles.statusDot} />
    {ativo ? "Ativo" : "Inativo"}
  </span>
));
StatusBadge.displayName = "StatusBadge";

/** Linha da tabela */
const UsuarioRow = memo(({ usuario, onEdit, onToggleStatus }) => (
  <tr className={styles.tableRow}>
    <td className={styles.tdNome}>
      <div className={styles.avatarCell}>
        <div className={styles.avatar} aria-hidden="true">
          {usuario.nome.charAt(0).toUpperCase()}
        </div>
        <span>{usuario.nome}</span>
      </div>
    </td>
    <td className={styles.tdLogin}>
      <code className={styles.loginCode}>{usuario.login}</code>
    </td>
    <td className={styles.tdCargo}>{usuario.cargo}</td>
    <td className={styles.tdPerfil}>
      <RoleBadge role={usuario.nivel_acesso} />
    </td>
    <td className={styles.tdStatus}>
      <StatusBadge ativo={usuario.ativo} />
    </td>
    <td className={styles.tdAcoes}>
      <div className={styles.actionGroup}>
        <button
          className={styles.btnIcon}
          onClick={() => onEdit(usuario)}
          aria-label={`Editar ${usuario.nome}`}
          title="Editar"
        >
          <EditIcon />
        </button>
        <button
          className={`${styles.btnIcon} ${usuario.ativo ? styles.btnDeactivate : styles.btnActivate}`}
          onClick={() => onToggleStatus(usuario)}
          aria-label={usuario.ativo ? `Desativar ${usuario.nome}` : `Ativar ${usuario.nome}`}
          title={usuario.ativo ? "Desativar" : "Ativar"}
        >
          {usuario.ativo ? <BlockIcon /> : <CheckIcon />}
        </button>
      </div>
    </td>
  </tr>
));
UsuarioRow.displayName = "UsuarioRow";

/** Campo de formulário genérico */
const FormField = memo(({ id, label, children, required }) => (
  <div className={styles.formGroup}>
    <label htmlFor={id} className={styles.formLabel}>
      {label}
      {required && <span className={styles.required} aria-hidden="true"> *</span>}
    </label>
    {children}
  </div>
));
FormField.displayName = "FormField";

/** Modal de criação/edição */
const UsuarioModal = memo(({ form, setForm, onSubmit, onClose, submitting }) => {
  const isEdit = Boolean(form.id);

  const handleChange = useCallback(
    (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value })),
    [setForm]
  );

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h3 id="modal-title" className={styles.modalTitle}>
            {isEdit ? "Editar Usuário" : "Novo Usuário"}
          </h3>
          <button
            type="button"
            className={styles.btnClose}
            onClick={onClose}
            aria-label="Fechar modal"
          >
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={onSubmit} className={styles.modalForm} noValidate>
          <div className={styles.formGrid}>
            <FormField id="nome" label="Nome completo" required>
              <input
                id="nome"
                type="text"
                className={styles.input}
                placeholder="Ex: João Silva"
                value={form.nome}
                onChange={handleChange("nome")}
                autoComplete="name"
                required
                disabled={submitting}
              />
            </FormField>

            <FormField id="login" label="Login" required>
              <input
                id="login"
                type="text"
                className={styles.input}
                placeholder="Ex: joao.silva"
                value={form.login}
                onChange={handleChange("login")}
                autoComplete="username"
                required
                disabled={submitting}
              />
            </FormField>

            <FormField
              id="senha"
              label={isEdit ? "Nova senha (deixe em branco para manter)" : "Senha"}
              required={!isEdit}
            >
              <input
                id="senha"
                type="password"
                className={styles.input}
                placeholder={isEdit ? "••••••••" : "Mínimo 6 caracteres"}
                value={form.senha}
                onChange={handleChange("senha")}
                autoComplete={isEdit ? "new-password" : "new-password"}
                required={!isEdit}
                disabled={submitting}
              />
            </FormField>

            <FormField id="cargo" label="Cargo" required>
              <input
                id="cargo"
                type="text"
                className={styles.input}
                placeholder="Ex: Coordenador de Vendas"
                value={form.cargo}
                onChange={handleChange("cargo")}
                required
                disabled={submitting}
              />
            </FormField>

            <FormField id="nivel_acesso" label="Perfil de acesso" required>
              <select
                id="nivel_acesso"
                className={styles.select}
                value={form.nivel_acesso}
                onChange={handleChange("nivel_acesso")}
                required
                disabled={submitting}
              >
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.btnCancel}
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.btnSave}
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? (
                <><span className={styles.spinner} aria-hidden="true" /> Salvando...</>
              ) : (
                isEdit ? "Salvar alterações" : "Criar usuário"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});
UsuarioModal.displayName = "UsuarioModal";

// ─── Icons ────────────────────────────────────────────────────────────────────

const EditIcon  = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const BlockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Usuarios = () => {
  const { usuarios, loading, error, refetch } = useUsuarios();

  const [modalOpen,  setModalOpen]  = useState(false);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [filtro,     setFiltro]     = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ── Filtered list (memoized) ─────────────────────────────────────────────────
  const usuariosFiltrados = useMemo(() => {
    const q = filtro.toLowerCase().trim();
    if (!q) return usuarios;
    return usuarios.filter((u) =>
      [u.nome, u.login, u.cargo, u.nivel_acesso].some((campo) =>
        campo?.toLowerCase().includes(q)
      )
    );
  }, [usuarios, filtro]);

  // ── Modal helpers ─────────────────────────────────────────────────────────────
  const openModal = useCallback((usuario = null) => {
    setForm(usuario ? { ...usuario, cliente_id: usuario.cliente_id ?? null } : EMPTY_FORM);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setForm(EMPTY_FORM);
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (submitting) return;

    const userLogado = JSON.parse(localStorage.getItem("usuario") ?? "null");
    const payload    = buildPayload(form, userLogado);

    setSubmitting(true);
    try {
      if (form.id) {
        await usuariosService.update(form.id, payload);
        Swal.fire({ icon: "success", title: "Usuário atualizado!", timer: 1500, showConfirmButton: false });
      } else {
        await usuariosService.create(payload);
        Swal.fire({ icon: "success", title: "Usuário criado!", timer: 1500, showConfirmButton: false });
      }
      refetch();
      closeModal();
    } catch (err) {
      Swal.fire("Erro", err.response?.data?.error || "Não foi possível salvar o usuário.", "error");
    } finally {
      setSubmitting(false);
    }
  }, [submitting, form, refetch, closeModal]);

  // ── Toggle status ──────────────────────────────────────────────────────────
  const handleToggleStatus = useCallback(async (usuario) => {
    const isActive = usuario.ativo;

    const result = await confirm({
      title:             isActive ? "Desativar usuário?" : "Ativar usuário?",
      text:              isActive
        ? "O usuário ficará inativo, mas não será excluído."
        : "O usuário voltará a ter acesso ao sistema.",
      icon:              isActive ? "warning" : "question",
      confirmButtonText: isActive ? "Sim, desativar" : "Sim, ativar",
      confirmButtonColor: isActive ? "#E53E3E" : "#1A56DB",
      cancelButtonText:  "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      if (isActive) {
        await usuariosService.deactivate(usuario.id);
      } else {
        await usuariosService.activate(usuario.id);
      }
      refetch();
      Swal.fire({
        icon: "success",
        title: isActive ? "Usuário desativado!" : "Usuário ativado!",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Erro", `Não foi possível ${isActive ? "desativar" : "ativar"} o usuário.`, "error");
    }
  }, [refetch]);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Usuários</h1>
          <p className={styles.pageSubtitle}>
            {usuarios.length} {usuarios.length === 1 ? "usuário cadastrado" : "usuários cadastrados"}
          </p>
        </div>
        <button
          className={styles.btnPrimary}
          onClick={() => openModal()}
          aria-label="Adicionar novo usuário"
        >
          <PlusIcon /> Novo usuário
        </button>
      </div>

      {/* Search */}
      <div className={styles.searchWrapper} role="search">
        <span className={styles.searchIcon} aria-hidden="true"><SearchIcon /></span>
        <input
          type="search"
          className={styles.searchInput}
          placeholder="Buscar por nome, login, cargo ou perfil..."
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          aria-label="Filtrar usuários"
        />
      </div>

      {/* States */}
      {loading && (
        <div className={styles.stateBox} role="status" aria-live="polite">
          <span className={styles.spinner} aria-hidden="true" />
          <span>Carregando usuários...</span>
        </div>
      )}

      {error && !loading && (
        <div className={styles.errorBox} role="alert">
          <p>{error}</p>
          <button className={styles.btnRetry} onClick={refetch}>Tentar novamente</button>
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <div className={styles.tableWrapper}>
          <table className={styles.table} aria-label="Tabela de usuários">
            <thead>
              <tr>
                <th scope="col">Nome</th>
                <th scope="col">Login</th>
                <th scope="col">Cargo</th>
                <th scope="col">Perfil</th>
                <th scope="col">Status</th>
                <th scope="col" className={styles.thAcoes}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuariosFiltrados.length > 0 ? (
                usuariosFiltrados.map((u) => (
                  <UsuarioRow
                    key={u.id}
                    usuario={u}
                    onEdit={openModal}
                    onToggleStatus={handleToggleStatus}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={6} className={styles.emptyState}>
                    Nenhum usuário encontrado para "{filtro}".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <UsuarioModal
          form={form}
          setForm={setForm}
          onSubmit={handleSubmit}
          onClose={closeModal}
          submitting={submitting}
        />
      )}
    </div>
  );
};

export default Usuarios;